import React, { useEffect, useState } from 'react'
import { useRecoilValue } from 'recoil'

import {
  useFallbackInsurance,
  useInsuranceOptions,
  useInsurances,
} from '../../../../common/hooks/useInsurance'
import { getIncidentType } from '../../../../common/utils/insurance'
import { selectedInsuranceAtom, selectedInsurancePayerAtom } from '../../../../state/common/atoms'
import useReserveState from '../../hooks/useReserveState'
import PohjolaSVG from '../../images/Pohjola.svg?react'
import { AccidentDetailsFormData, DirectCompensationInformation } from '../../utils/insurance'

import PohjolaCompanyDetailsForm from './AccidentDetailsForm'
import CustomershipCheck from './CustomershipCheck'
import CustomershipCheckCompanyFlow from './CustomershipCheckCompanyFlow'
import PohjolaContents from './PohjolaContents'
import { Container } from './Shared'

interface Props {
  additionalInfoForm: string
  onSubmit(data?: DirectCompensationInformation): void
  onBack(): void
  onContinueAsSelfPaying(): void
}

// View type to track the current view
type ViewState = 'customershipCheck' | 'insuranceContents' | 'insuranceDetails'
const PohjolaForm: React.FC<React.PropsWithChildren<Props>> = ({
  additionalInfoForm,
  onSubmit,
  onBack,
  onContinueAsSelfPaying,
}) => {
  const { selectedUser } = useReserveState()
  const [currentView, setCurrentView] = useState<ViewState>('customershipCheck')
  const [selectedPermitId, setSelectedPermitId] = useState<string | undefined>(undefined)
  const selectedInsurance = useRecoilValue(selectedInsuranceAtom)
  const selectedInsurancePayer = useRecoilValue(selectedInsurancePayerAtom)
  const { insurances } = useInsurances()
  const { redirectToFallbackInsurance } = useFallbackInsurance()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentView])

  const isCompanyForm =
    additionalInfoForm === 'pohjola_company_form' ||
    additionalInfoForm === 'pohjola_company_form_accident'
  const translationKey = isCompanyForm ? 'pohjola_company_form' : 'pohjolaForm'

  const insurancesApiInsuranceCompanyId = isCompanyForm ? 'op-yritys' : 'op'
  const incidentType = getIncidentType(insurances, selectedInsurance, selectedInsurancePayer)

  const {
    insuranceOptions: options,
    pending: optionsPending,
    error: optionsError,
  } = useInsuranceOptions(selectedUser.omUid, insurancesApiInsuranceCompanyId, incidentType)

  useEffect(() => {
    if (options && options.checkCustomership === false) {
      onSubmit()
    }
  }, [options, onSubmit])

  if (optionsPending) {
    return null
  }

  if (optionsError) {
    throw 'Error fetching insurance options'
  }

  // Check if we should show insurance contents
  const handleCustomershipSuccess = () => {
    if (
      additionalInfoForm === 'pohjola_company_form' ||
      additionalInfoForm === 'pohjola_company_form_accident'
    ) {
      setCurrentView('insuranceContents')
    } else {
      // If no form needed, proceed directly
      onSubmit()
    }
  }

  const handleRedirectToFallbackInsurance = () => {
    // Set view to customershipCheck to reset the flow
    setCurrentView('customershipCheck')
    // Call the fallback hook to handle the insurance change
    redirectToFallbackInsurance(selectedInsurancePayer, onContinueAsSelfPaying)
  }

  const handleInsuranceContentsSubmit = (permitId: string) => {
    setSelectedPermitId(permitId)
    setCurrentView('insuranceDetails')
  }

  const userDataForPermit = options?.userDataForPermit
  const handleInsuranceDetailsSubmit = (data: AccidentDetailsFormData[]) => {
    // Submit with the selected permit ID and incidentDate
    if (selectedPermitId) {
      onSubmit({
        permitId: selectedPermitId,
        userFormData: data,
      })
    }
  }

  // Conditional rendering based on the current view
  return (
    <React.Fragment>
      {currentView === 'customershipCheck' && !isCompanyForm && (
        <Container>
          <CustomershipCheck
            Logo={PohjolaSVG}
            translationKey={translationKey}
            insurancesApiInsuranceCompanyId={insurancesApiInsuranceCompanyId}
            subjectOmUid={selectedUser.omUid}
            onBack={onBack}
            onSuccess={handleCustomershipSuccess}
            onContinueAsSelfPaying={onContinueAsSelfPaying}
          />
        </Container>
      )}
      {currentView === 'customershipCheck' && isCompanyForm && options && (
        <Container>
          <CustomershipCheckCompanyFlow
            Logo={PohjolaSVG}
            translationKey={translationKey}
            insurancesApiInsuranceCompanyId={insurancesApiInsuranceCompanyId}
            subjectOmUid={selectedUser.omUid}
            incidentType={incidentType}
            onBack={onBack}
            onSuccess={handleCustomershipSuccess}
            onContinueAsSelfPaying={onContinueAsSelfPaying}
            redirectToPrivateInsurance={handleRedirectToFallbackInsurance}
            insuranceOptions={options}
          />
        </Container>
      )}
      {currentView === 'insuranceContents' && (
        <Container>
          <PohjolaContents
            translationKey={translationKey}
            subjectOmUid={selectedUser.omUid}
            insuranceCompanyId={insurancesApiInsuranceCompanyId}
            incidentType={incidentType}
            onSubmit={handleInsuranceContentsSubmit}
            onBack={() => setCurrentView('customershipCheck')}
            onContinueAsSelfPaying={onContinueAsSelfPaying}
          />
        </Container>
      )}
      {currentView === 'insuranceDetails' && selectedPermitId && (
        <div>
          <PohjolaCompanyDetailsForm
            translationKey={translationKey}
            userDataForPermit={userDataForPermit}
            onSubmit={handleInsuranceDetailsSubmit}
            onBack={() => setCurrentView('insuranceContents')}
          />
        </div>
      )}
    </React.Fragment>
  )
}

export default PohjolaForm
